import { supabase } from "@/lib/supabase";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import OrderHeader from "./order/OrderHeader";
import PDF from "./order/PDF";
import { Button } from "@/components/ui/button";
import { DownloadIcon, EyeIcon, LoaderCircleIcon, SendHorizonalIcon, UploadIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const { APP_API_URL } = import.meta.env

export default function Invoice() {
    const { invoice_id } = useParams()
    const { session } = useAuth()

    const intervalGeneratedRef = useRef(null);
    const [invoice, setInvoice] = useState();
    const [orderCount, setOrderCount] = useState(0);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [loadingUpload, setLoadingUpload] = useState(false);
    const [loadingSend, setLoadingSend] = useState(false);
    const [loadingClose, setLoadingClose] = useState(false);

    const downloadFile = async () => {
        try {
            setLoadingDownload(true)
            if (!invoice || !invoice.path) {
                throw new Error('Invoice not found or path missing');
            }

            const { data, error } = await supabase.storage
                .from('invoices')
                .download(invoice.path + '?t=' + Date.now());

            if (error) {
                throw error;
            }

            // Create a blob from the data
            const blob = new Blob([data], { type: 'application/pdf' });

            // Create a download link and trigger the download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            link.download = `${Date.now()}-${invoice.path}`;

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setLoadingDownload(false)
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const getInvoice = async () => {
        const { data: invoice } = await supabase.from('invoices').select('*, company:companies(*, settings:company_settings(*))').eq('id', invoice_id).single();
        const { count } = await supabase.from('folders').select('id', { count: 'exact', head: true }).eq('invoice_id', invoice_id);

        setInvoice(invoice)
        setOrderCount(count || 0);
    };

    const generate = async () => {
        setLoadingClose(true)

        const response = await fetch(`${APP_API_URL}/invoice/${invoice.company.id}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
                close: false
            }),
        })

        if (!response.ok) {
            alert('KO')
        }

        pollNewInvoice(true)
    };

    const send = async () => {
        setLoadingSend(true)

        const response = await fetch(`${APP_API_URL}/send-invoice/${invoice.id}`)

        if (!response.ok) {
            alert('KO')
        } else {
            setInvoice({
                ...invoice,
                current: false
            })
        }


        setLoadingSend(false)
    };

    const pollNewInvoice = (close = false) => {
        intervalGeneratedRef.current = setInterval(async () => {
            try {
                const { data, error } = await supabase
                    .from('invoices')
                    .select('generated')
                    .eq('id', invoice.id)
                    .single()

                console.log('Fetching invoice...')

                if (data?.generated) {
                    await getInvoice()
                    setTimeout(() => {
                        if (close) {
                            setLoadingClose(false)
                        } else {
                            setLoadingUpload(false)
                        }
                    }, 3000)

                    clearInterval(intervalGeneratedRef.current);
                }
            } catch (error) {
                console.error('Error fetching report:', error)
            }
        }, 2000)
    }

    useEffect(() => {
        getInvoice();

        return () => {
            if (intervalGeneratedRef.current) {
                clearInterval(intervalGeneratedRef.current);
            }
        };
    }, []);

    const formatCadPrice = (amount: number): string => {
        return new Intl.NumberFormat('fr-CA', {
            style: 'currency',
            currency: 'CAD',
            minimumFractionDigits: 2
        }).format(amount);
    }

    const fileRef = React.useRef(null)

    const uploadFile = async (file) => {
        if (!file) {
            return;
        }

        setLoadingUpload(true)

        const formData = new FormData()
        formData.append(`xlsx`, file, file.name)

        const { error } = await fetch(`${APP_API_URL}/update-invoice/${invoice.id}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
            body: formData,
        }).then(res => res.json())

        pollNewInvoice()
    }

    return (
        <div className="flex flex-col h-screen min-h-0">
            <OrderHeader title={!invoice ? 'Facture' : `Facture ${invoice.company.name} ${invoice.reference ? 'n° ' + invoice.reference : ''}`} />

            {invoice && <>
                <input accept=".xlsx" ref={fileRef} type="file" style={{ display: 'none' }} onChange={(event) => uploadFile(event.target.files[0])} />

                <div className="flex items-center justify-between p-4 pb-0">
                    <div className="flex items-center gap-2">
                        <Button variant={"secondary"} onClick={downloadFile} disabled={loadingDownload || !invoice.total}>
                            {(loadingDownload) && (
                                <LoaderCircleIcon className=" h-4 w-4 animate-spin" />
                            )}

                            {!loadingDownload && <DownloadIcon className=" h-4 w-4 " />}

                            Télécharger
                        </Button>
                        {invoice.current && <Button variant={"secondary"} disabled={loadingUpload || !invoice.total} onClick={() => fileRef.current.click()}>
                            {(loadingUpload) && (
                                <LoaderCircleIcon className=" h-4 w-4 animate-spin" />
                            )}

                            {!loadingUpload && <UploadIcon className=" h-4 w-4 " />}

                            Téléverser
                        </Button>}
                    </div>

                    {!loadingUpload && <div className="flex flex-col items-center">
                        <span className="text-xl font-semibold">
                            {formatCadPrice(invoice.total)}
                        </span>
                        {!!orderCount && <span className="text-xs">{orderCount} commande{orderCount > 1 ? 's' : ''}</span>}
                    </div>}

                    <div className="flex items-center gap-2">
                        {invoice.current && <>
                            <Button variant={"secondary"} disabled={!invoice.total || loadingSend || loadingClose} onClick={() => generate()}>
                                {(loadingClose) && (
                                    <LoaderCircleIcon className=" h-4 w-4 animate-spin" />
                                )}

                                {!loadingClose && <EyeIcon className=" h-4 w-4 " />}

                                Générer
                            </Button>


                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button disabled={!invoice.total || loadingSend || loadingClose || !invoice.reference} onClick={() => send()}>
                                        {(loadingSend) && (
                                            <LoaderCircleIcon className=" h-4 w-4 animate-spin" />
                                        )}

                                        {!loadingSend && <SendHorizonalIcon className=" h-4 w-4 " />}

                                        Envoyer
                                    </Button>
                                </TooltipTrigger>

                                <TooltipContent side="left">
                                    <p>{invoice.company.settings.invoice_email}</p>
                                </TooltipContent>
                            </Tooltip>
                        </>}
                        {!invoice.current && <span className="flex items-center gap-2">
                            <SendHorizonalIcon className="h-4 w-4" />
                            Envoyé le {format(new Date(invoice.sent_at), "d MMMM yyyy", { locale: fr })}
                        </span>}
                    </div>
                </div>

                {<div className="flex-grow flex gap-4 min-h-0 p-4">
                    <PDF displayLoading={loadingUpload || loadingSend || loadingClose} path={invoice.path.replace('xlsx', 'pdf')} invoice />
                </div>}
            </>}
        </div>
    );
}